import { emitSessionExpiredEvent } from './adminUi';

// Keep the existing portal domain normalization; the API is authoritative for validation.
export const normalizeDomain = (value) => String(value ?? '').trim().toLowerCase()
  .replace(/^https?:\/\//i, '').replace(/\/+$/g, '');

export const getDomainConfigurations = (site, replayPolicy) => (
  [...new Set((site.domainNames || []).map(normalizeDomain).filter(Boolean))].map(domain => {
    const saved = (site.domainConfigurations || []).find(item => normalizeDomain(item.domain) === domain);
    const item = {
      domain,
      consumerExperience: saved?.consumerExperience === true,
      heat_map: saved?.heat_map === true,
      customer_analytics: saved?.customer_analytics === true
    };
    if (saved?.session_replay !== undefined) {
      item.session_replay = saved.session_replay === true;
    } else if (replayPolicy?.sessionReplayRequested !== undefined) {
      item.session_replay = Boolean(item.heat_map && item.customer_analytics && replayPolicy.sessionReplayRequested);
    }
    return item;
  })
);

// Omission on metadata/status updates preserves server policy, including legacy responses.
export const buildSitePayload = (site, organizationId) => ({
  ...(site.id ? { id: site.id } : {}),
  name: site.name,
  description: site.description,
  domainNames: [...new Set((site.domainNames || []).map(normalizeDomain).filter(Boolean))],
  organizationId,
  previewBaseUrl: (site.previewBaseUrl || '').trim(),
  isActive: site.isActive,
  ...(Array.isArray(site.domainConfigurations)
    ? {
        domainConfigurations: getDomainConfigurations(site)
          .filter(item => !item.domain.includes('*'))
          .map(({ session_replay, ...rest }) => rest)
      }
    : {})
});

export const getOriginLimit = (entitlements) => {
  const limits = Array.isArray(entitlements?.limits) ? entitlements.limits : [];
  const policy = limits.find(item => item.limitKey === 'extension_origins');
  if (policy?.isUnlimited === true) return -1;
  const value = policy?.value ?? policy?.allowed;
  // -1 is an internal display sentinel only; the API grants unlimited explicitly.
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
};

export const canEnableDomainCapability = (flag, configuration, entitlements, isSiteStorageReady = true) => {
  if (normalizeDomain(configuration.domain).includes('*')) return false;
  const features = entitlements?.enabledFeatures || [];
  if (!features.includes('extension_player') || getOriginLimit(entitlements) === null) return false;
  if (flag === 'consumerExperience') return true;
  if (!features.includes('heat_map')) return false;
  if (flag === 'heat_map') return true;
  if (flag === 'customer_analytics') {
    return configuration.heat_map && features.includes('customer_analytics');
  }
  if (flag === 'session_replay') {
    return (
      Boolean(configuration.heat_map) &&
      Boolean(configuration.customer_analytics) &&
      features.includes('heat_map') &&
      features.includes('customer_analytics') &&
      Boolean(isSiteStorageReady)
    );
  }
  return false;
};

/**
 * Enforces strict hierarchical capability dependencies:
 * session_replay -> customer_analytics -> heat_map
 *
 * @param {object} configuration 
 * @param {string} flag - 'consumerExperience' | 'heat_map' | 'customer_analytics' | 'session_replay'
 * @param {boolean} checked 
 * @returns {object} updated configuration
 */
export const enforceCapabilityDependencies = (configuration, flag, checked) => {
  const updated = { ...configuration, [flag]: checked };

  if (flag === 'session_replay') {
    updated.session_replay = checked;
    if (checked) {
      updated.customer_analytics = true;
      updated.heat_map = true;
    }
  } else if (flag === 'customer_analytics') {
    if (checked) {
      updated.heat_map = true;
    } else if (configuration.session_replay !== undefined) {
      updated.session_replay = false;
    }
  } else if (flag === 'heat_map') {
    if (!checked) {
      updated.customer_analytics = false;
      if (configuration.session_replay !== undefined) {
        updated.session_replay = false;
      }
    }
  }

  return updated;
};

export const countEnabledOrigins = (configurations) => new Set(configurations
  .filter(item => item.consumerExperience || item.heat_map)
  .map(item => normalizeDomain(item.domain))).size;

export const readSitePolicyError = async (response, fallbackMessage) => {
  if (response.status === 401) emitSessionExpiredEvent();
  let body;
  const fallbackResponse = response.clone ? response.clone() : response;
  try { body = await response.json(); } catch { body = null; }
  if (!body) {
    try { body = await fallbackResponse.text(); } catch { body = {}; }
  }
  const code = typeof body?.error === 'string' ? body.error : body?.code || body?.error?.code;
  const requestNotStarted = response.status === 503 && body?.code === 'extension_policy_not_started' &&
    body.requestStarted === false && !body.operationId && !body.siteCollectionId && !body.siteId && !body.id;
  const isQuotaExceeded = typeof code === 'string' && ['quotaexceeded', 'quota_exceeded'].includes(code.toLowerCase());
  let category = 'Save failed';
  if (requestNotStarted) category = 'Request not started';
  else if (isQuotaExceeded) category = body?.limitKey === 'extension_origins' ? 'Origin quota exceeded' : 'Quota exceeded';
  else if (response.status === 429) category = 'Request throttled';
  else if (/entitlement|licens/i.test(code || '')) category = 'Entitlement denied';
  else if (response.status === 401 || response.status === 403) category = 'Permission denied';
  else if (response.status === 409 || response.status === 412) category = 'Configuration conflict';
  else if (response.status === 202 || response.status >= 500) category = 'Configuration unavailable or pending';
  const validationMessages = body?.errors && typeof body.errors === 'object'
    ? Object.values(body.errors).flat().map(item => typeof item === 'string' ? item : item?.errorMessage || item?.message).filter(Boolean).join(' ')
    : '';
  const message = body?.message || body?.error?.message || body?.detail || validationMessages ||
    body?.title || (typeof body === 'string' ? body : '') || fallbackMessage;
  const quotaDetail = isQuotaExceeded && typeof body?.requestedCount === 'number'
    ? ` Requested ${body.limitKey === 'extension_origins' ? 'distinct origins' : 'count'}: ${body.requestedCount}; limit: ${typeof body.limit === 'number' ? body.limit : 'unavailable'}.` : '';
  const retryAfter = !isQuotaExceeded && response.status === 429 ? response.headers?.get('Retry-After') : null;
  const throttleDetail = !isQuotaExceeded && response.status === 429
    ? ` Wait before retrying.${retryAfter ? ` Retry-After: ${retryAfter}.` : ''}` : '';
  const operationDetail = typeof body?.operationId === 'string' ? ` Operation ID: ${body.operationId}.` : '';
  const retryGuidance = requestNotStarted
    ? 'No writes started. You may explicitly submit the retained draft again.'
    : 'Reload server status before retrying.';
  const error = new Error(`${category}${code ? ` (${code})` : ''}: ${message || 'The server has not confirmed this change.'}${quotaDetail}${throttleDetail}${operationDetail} Your draft has been retained. ${retryGuidance}`);
  error.status = response.status;
  error.code = code;
  error.requestNotStarted = requestNotStarted;
  error.siteId = body?.siteCollectionId || body?.id;
  error.operationId = body?.operationId;
  error.isBlockingOperation = typeof body?.isBlockingOperation === 'boolean' ? body.isBlockingOperation : null;
  return error;
};
